import { IDockviewPanelProps } from "dockview-react";
import { useEffect, useMemo, useRef, useState } from "react";
import Plotter from "./utils/Plotter";
import { resolveEndpoint } from "./utils/utils";

interface IUnderlyingCandle {
  ts_event: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

interface IOptionCandle {
  ts_event: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  iv_open: number;
  iv_high: number;
  iv_low: number;
  iv_close: number;
  iv_mid: number;
  readable_name: string;
}

interface IContractData {
  contract: string;
  readableName: string;
  data: IOptionCandle[];
}

const COLORS = [
  "#1f77b4", "#ff7f0e", "#2ca02c", "#d62728",
  "#9467bd", "#8c564b", "#e377c2", "#17becf",
];

/**
 * If the string is a bare date (YYYY-MM-DD), extend it to cover the full day.
 * Otherwise leave it as-is (caller already includes HH:MM).
 */
function toXBound(dateStr: string, isEnd: boolean): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr.trim())) {
    return isEnd ? `${dateStr} 23:59` : `${dateStr} 00:00`;
  }
  return dateStr;
}

export const IVPlot = (props: IDockviewPanelProps) => {
  const params = props.params as {
    ticker: string;
    contracts: string[];
    start_str: string;
    end_str: string;
    interval: string;
  };

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [progressMsg, setProgressMsg] = useState("");
  const [underlying, setUnderlying] = useState<IUnderlyingCandle[]>([]);
  const [contracts, setContracts] = useState<IContractData[]>(
    (params.contracts ?? []).map((c) => ({ contract: c, readableName: c.trim(), data: [] }))
  );
  const [selectedContracts, setSelectedContracts] = useState<Set<string>>(
    new Set(params.contracts?.length ? [params.contracts[0]] : [])
  );

  // Compute x-axis bounds once from params
  const xStart = toXBound(params.start_str, false);
  const xEnd   = toXBound(params.end_str,   true);

  useEffect(() => {
    props.api.setTitle(`IV OHLCV — ${params.ticker}`);
    if (!params.start_str || !params.end_str) {
      setError("Start date and end date are required.");
      return;
    }
    if (!params.contracts?.length) {
      setError("No contracts selected.");
      return;
    }
    fetchData();
    // params and fetchData are stable for the lifetime of this panel (dockview initialisation)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchData() {
    setIsLoading(true);
    setError("");
    setProgressMsg("");

    const token = localStorage.getItem("token");
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) headers["authorization"] = `Bearer ${token}`;

    try {
      const response = await fetch(`${resolveEndpoint()}/multi-iv-ohlcv`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          ticker: params.ticker,
          contracts: params.contracts,
          start_str: params.start_str,
          end_str: params.end_str,
          interval: params.interval,
        }),
      });

      if (!response.ok || !response.body) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop()!;

        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const event = JSON.parse(part.slice(6));

          if (event.type === "progress") {
            setProgressMsg(event.message);
          } else if (event.type === "data") {
            setProgressMsg("");
            setUnderlying(event.underlying);
            const parsed: IContractData[] = (event.options as Array<Record<string, IOptionCandle[]>>).map(
              (entry) => {
                const contract = Object.keys(entry)[0];
                const data = entry[contract];
                return {
                  contract,
                  readableName: data[0]?.readable_name ?? contract,
                  data,
                };
              }
            );
            setContracts(parsed);
            if (parsed.length > 0) {
              setSelectedContracts(new Set([parsed[0].contract]));
            }
          } else if (event.type === "error") {
            setError(event.detail);
            break;
          }
        }
      }
    } catch (err: any) {
      setError(err?.message || "Request failed");
    } finally {
      setIsLoading(false);
    }
  }

  function toggleContract(contract: string) {
    setSelectedContracts((prev) => {
      const next = new Set(prev);
      if (next.has(contract)) next.delete(contract);
      else next.add(contract);
      return next;
    });
  }

  // ── Shared axis config ────────────────────────────────────────────────────
  const baseXAxis = {
    autorange: false,
    range: [xStart, xEnd],
    type: "date",
    rangeslider: { visible: false },
    // d3 tick labels with abbreviated day: "Mon 2024-02-28"
    tickformat: "%a %Y-%m-%d",
    showspikes: true,
    spikesnap: "cursor",
    spikecolor: "#999",
    spikethickness: 1,
    spikemode: "toaxis+across+marker",
    spikedash: "solid",
    showgrid: true,
  };

  const baseYAxis = {
    autorange: true,
    showgrid: true,
    showspikes: true,
    spikemode: "toaxis+across+marker",
    spikesnap: "cursor",
    spikedash: "solid",
    spikecolor: "#999",
    spikethickness: 1,
  };

  // ── Chart 1: Underlying candlestick ───────────────────────────────────────
  const underlyingTraces = useMemo<any[]>(() => {
    if (!underlying.length) return [];
    return [
      {
        type: "candlestick",
        x: underlying.map((d) => d.ts_event),
        open:  underlying.map((d) => d.open),
        high:  underlying.map((d) => d.high),
        low:   underlying.map((d) => d.low),
        close: underlying.map((d) => d.close),
        name: params.ticker,
        increasing: { line: { color: "#26a69a" } },
        decreasing: { line: { color: "#ef5350" } },
        hovertemplate:
          `<b>${params.ticker}</b><br>` +
          "%{x|%Y-%m-%d %H:%M (%a)}<br>" +
          "O: %{open:.3f}  H: %{high:.3f}<br>" +
          "L: %{low:.3f}  C: %{close:.3f}" +
          "<extra></extra>",
      },
    ];
  }, [underlying, params.ticker]);

  const undLayout = {
    dragmode: "zoom",
    autosize: true,
    margin: { l: 60, r: 10, t: 10, b: 40 },
    showlegend: false,
    hovermode: "x unified" as const,
    xaxis: baseXAxis,
    yaxis: { ...baseYAxis, title: "Price" },
  };

  // ── Chart 2: Options OHLCV candlestick + IV mid overlay ──────────────────
  const optionCandleTraces = useMemo<any[]>(() => {
    const traces: any[] = [];
    contracts.forEach((c, i) => {
      if (!selectedContracts.has(c.contract)) return;
      const color = COLORS[i % COLORS.length];

      // Candlestick — visual only; hovertemplate is ignored by Plotly for
      // candlestick in x-unified mode, so we delegate hover to the invisible
      // scatter below.
      traces.push({
        type: "candlestick",
        x:     c.data.map((d) => d.ts_event),
        open:  c.data.map((d) => d.open),
        high:  c.data.map((d) => d.high),
        low:   c.data.map((d) => d.low),
        close: c.data.map((d) => d.close),
        name: c.readableName,
        increasing: { line: { color } },
        decreasing: { line: { color } },
        yaxis: "y",
        hoverinfo: "skip",
      });

      // Invisible scatter that owns the OHLC hover for this contract
      traces.push({
        type: "scatter",
        mode: "markers",
        x: c.data.map((d) => d.ts_event),
        y: c.data.map((d) => d.close),
        marker: { opacity: 0, size: 1, color },
        showlegend: false,
        yaxis: "y",
        customdata: c.data.map((d) => [d.open, d.high, d.low, d.close]),
        hovertemplate:
          `<b>${c.readableName}</b><br>` +
          "%{x|%Y-%m-%d %H:%M (%a)}<br>" +
          "O: %{customdata[0]:.3f}  H: %{customdata[1]:.3f}<br>" +
          "L: %{customdata[2]:.3f}  C: %{customdata[3]:.3f}" +
          "<extra></extra>",
      });

      // Line connecting open prices of each candle
      traces.push({
        type: "scatter",
        mode: "lines",
        x: c.data.map((d) => d.ts_event),
        y: c.data.map((d) => d.open),
        name: `${c.readableName} open`,
        line: { color, width: 1, dash: "solid" },
        showlegend: false,
        yaxis: "y",
        hoverinfo: "skip",
      });

      traces.push({
        type: "scatter",
        mode: "lines",
        x: c.data.map((d) => d.ts_event),
        y: c.data.map((d) => d.iv_mid),
        name: `${c.readableName} IV mid`,
        line: { color, width: 1.5, dash: "dot" },
        yaxis: "y2",
        hovertemplate:
          `<b>${c.readableName} — IV Mid</b><br>` +
          "%{x|%Y-%m-%d %H:%M (%a)}<br>" +
          "IV Mid: %{y:.3f}" +
          "<extra></extra>",
      });
    });
    return traces;
  }, [contracts, selectedContracts]);

  const optLayout = {
    dragmode: "zoom",
    autosize: true,
    margin: { l: 60, r: 60, t: 10, b: 40 },
    showlegend: false,
    hovermode: "x unified" as const,
    xaxis: baseXAxis,
    yaxis: { ...baseYAxis, title: "Option Price" },
    yaxis2: {
      autorange: true,
      overlaying: "y",
      side: "right",
      title: "IV",
      showgrid: false,
      showspikes: false,
    },
  };

  // ── Chart 3: IV OHLCV lines ───────────────────────────────────────────────
  const ivTraces = useMemo<any[]>(() => {
    const traces: any[] = [];
    contracts.forEach((c, i) => {
      if (!selectedContracts.has(c.contract)) return;
      const color = COLORS[i % COLORS.length];

      const ivFields: Array<{ key: keyof IOptionCandle; dash: string; label: string }> = [
        { key: "iv_mid",   dash: "solid",   label: "IV Mid" },
        { key: "iv_open",  dash: "dash",    label: "IV Open" },
        { key: "iv_high",  dash: "dot",     label: "IV High" },
        { key: "iv_low",   dash: "dot",     label: "IV Low" },
        { key: "iv_close", dash: "dashdot", label: "IV Close" },
      ];

      ivFields.forEach(({ key, dash, label }) => {
        traces.push({
          type: "scatter",
          mode: "lines",
          x: c.data.map((d) => d.ts_event),
          y: c.data.map((d) => d[key] as number),
          name: `${c.readableName} — ${label}`,
          line: { color, width: 1, dash },
          hovertemplate:
            `<b>${c.readableName} — ${label}</b><br>` +
            "%{x|%Y-%m-%d %H:%M (%a)}<br>" +
            `${label}: %{y:.3f}` +
            "<extra></extra>",
        });
      });
    });
    return traces;
  }, [contracts, selectedContracts]);

  // Legend lives below the x-axis; bottom margin makes room for it
  const ivLayout = {
    dragmode: "zoom",
    autosize: true,
    margin: { l: 60, r: 10, t: 10, b: 160 },
    showlegend: true,
    legend: {
      orientation: "h" as const,
      y: -0.25,
      x: 0,
      xanchor: "left",
      yanchor: "top",
      font: { size: 10 },
    },
    hovermode: "x unified" as const,
    xaxis: baseXAxis,
    yaxis: { ...baseYAxis, title: "IV" },
  };

  const hasData = !isLoading && underlying.length > 0;

  return (
    <div className="overflow-y-auto h-full p-2">
      {error && <p className="text-red-500 mb-2">Error: {error}</p>}
      {isLoading && (
        <div className="mb-2">
          <p className="text-gray-500">Loading…</p>
          {progressMsg && <p className="text-xs text-gray-400 mt-1">{progressMsg}</p>}
        </div>
      )}

      {/* Contract selector — shared legend for all 3 charts */}
      {contracts.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {contracts.map((c, i) => {
            const active = selectedContracts.has(c.contract);
            const color = COLORS[i % COLORS.length];
            return (
              <button
                key={c.contract}
                type="button"
                onClick={() => toggleContract(c.contract)}
                className="px-2 py-1 text-xs border rounded transition-colors"
                style={
                  active
                    ? { backgroundColor: color, borderColor: color, color: "#fff" }
                    : { borderColor: color, color: "#666", backgroundColor: "#fff" }
                }
              >
                {c.readableName}
              </button>
            );
          })}
        </div>
      )}

      {hasData && (
        <>
          <section>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
              Underlying — {params.ticker}
            </p>
            <Plotter data={underlyingTraces} layout={undLayout} isLoading={isLoading} />
          </section>

          <section className="mt-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
              Options OHLCV + IV Mid
            </p>
            <Plotter data={optionCandleTraces} layout={optLayout} isLoading={isLoading} />
          </section>

          <section className="mt-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
              IV OHLCV
            </p>
            <Plotter data={ivTraces} layout={ivLayout} isLoading={isLoading} />
          </section>
        </>
      )}
    </div>
  );
};

// const Plotter = ({ data, layout }: { data: any[]; layout: any }) => {
//   const idRef = useRef(Math.random().toString(36).slice(2));
//   if (!data.length) {
//     return <p className="text-gray-400 text-sm py-6 text-center">No contract selected</p>;
//   }
//   return (
//     <Plot
//       data={data}
//       layout={{ ...layout, uirevision: idRef.current }}
//       useResizeHandler
//       style={{ width: "100%" }}
//     />
//   );
// };
